import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Building2, ReceiptText, CalendarRange, Mail } from "lucide-react";

export default function ClientTasksDialog({ entityId, onClose, onChanged }) {
  const { t, fmt } = useApp();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); });
  const [sending, setSending] = useState(false);
  const load = () => api.get("/tasks/by-client").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get(`/tasks/client-report?month=${month}`).then((r) => setReport(r.data)).catch(() => {}); }, [month]);
  const sendReport = async () => {
    setSending(true);
    try { await api.post("/tasks/client-report/send", { month }); toast.success(t("task.reportSent")); }
    catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); } finally { setSending(false); }
  };

  const invoice = async (g) => {
    setBusy(g.contact_id);
    try {
      await api.post("/tasks/invoice-from-client", { contact_id: g.contact_id, entity_id: g.entity_id || entityId });
      toast.success(t("task.invoiceCreated"), { action: { label: "→", onClick: () => navigate("/invoices") } });
      load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || t("msg.error")); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="client-tasks-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" />{t("task.byClient")}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[65vh] overflow-y-auto">
          {rows.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="client-tasks-empty">{t("common.noData")}</div>}
          {rows.map((g) => (
            <div key={g.contact_id} className="flex items-center gap-3 rounded-md border border-border p-3" data-testid={`client-row-${g.contact_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{g.contact_name || g.contact_id}</div>
                <div className="flex flex-wrap gap-1.5 mt-1 text-[10px]">
                  <Badge variant="secondary">{g.open} {t("dash.wlOpen")}</Badge>
                  <Badge variant="secondary">✓ {g.done}</Badge>
                  {g.hours > 0 && <Badge variant="outline" className="font-mono" data-testid={`client-hours-${g.contact_id}`}>{g.hours.toFixed(2)} h</Badge>}
                  {g.unbilled_amount > 0 && <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30 font-mono" data-testid={`client-unbilled-${g.contact_id}`}>{fmt(g.unbilled_amount)} {t("task.unbilled")}</Badge>}
                  {g.billed_amount > 0 && <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30 font-mono">{fmt(g.billed_amount)} {t("task.billed").toLowerCase()}</Badge>}
                </div>
              </div>
              <Button size="sm" onClick={() => invoice(g)} disabled={busy === g.contact_id || g.unbilled_amount <= 0} data-testid={`client-invoice-${g.contact_id}`}>
                <ReceiptText className="h-3.5 w-3.5 mr-1" />{t("task.createInvoice")}
              </Button>
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-3 space-y-2" data-testid="client-report">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-medium"><CalendarRange className="h-4 w-4 text-primary" />{t("task.report")}
              <Input type="month" className="h-8 w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month-input" /></div>
            <Button size="sm" variant="outline" onClick={sendReport} disabled={sending || !report?.rows?.length} data-testid="report-send-button"><Mail className="h-3.5 w-3.5 mr-1" />{t("task.reportSend")}</Button>
          </div>
          {report && (
            <div className="grid grid-cols-4 gap-2 text-center text-xs" data-testid="report-totals">
              <div className="rounded bg-muted/50 p-2"><div className="text-muted-foreground">{t("task.reportDone")}</div><div className="font-mono text-base">{report.totals.done}</div></div>
              <div className="rounded bg-muted/50 p-2"><div className="text-muted-foreground">{t("task.reportHours")}</div><div className="font-mono text-base">{report.totals.hours.toFixed(2)}</div></div>
              <div className="rounded bg-muted/50 p-2"><div className="text-muted-foreground">{t("task.reportBilled")}</div><div className="font-mono text-base text-emerald-500">{fmt(report.totals.billed)}</div></div>
              <div className="rounded bg-muted/50 p-2"><div className="text-muted-foreground">{t("task.reportUnbilled")}</div><div className="font-mono text-base text-amber-500">{fmt(report.totals.unbilled)}</div></div>
            </div>
          )}
          {report?.rows?.length > 0 && (
            <div className="max-h-32 overflow-y-auto text-xs divide-y divide-border">
              {report.rows.map((r) => (
                <div key={r.contact_id} className="flex justify-between gap-2 py-1" data-testid={`report-row-${r.contact_id}`}>
                  <span className="truncate">{r.contact_name}</span>
                  <span className="font-mono text-muted-foreground shrink-0">{r.done} ✓ · {r.hours.toFixed(2)} h · {fmt(r.billed)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
